import metadata from 'libphonenumber-js/metadata.full.json';

/**
 * Check if property is empty
 *
 * @param data - The data to check
 * @param excludes - Array of values to exclude from the empty check
 * @returns {boolean} - Returns true if the data is considered empty
 */
export function empty(data: any, excludes: any[] = []): boolean {
    if (data === '0') {
        return true;
    }
    if (typeof data === 'number' || typeof data === 'boolean') {
        return false;
    }
    if ((typeof data === 'undefined' || data === null) && !excludes.includes(null)) {
        return true;
    }
    if (typeof data?.length !== 'undefined') {
        return data?.length === 0;
    }

    return false;
}

/**
 * Check if string is possibly phone number
 *
 * @param phone
 * @returns {boolean}
 */
export const isAPhoneNumber = (phone: string) =>
    Object.values(metadata.countries).reduce((acc, country) => {
        const regex = new RegExp(country[2], 'g');

        if (regex.test(phone)) {
            acc.push(country);
        }

        return acc;
    }, []).length > 0;

/**
 * Check if string is possibly email
 * @param email
 * @returns {boolean}
 */
export const isAMail = (email: string) => {
    const regex =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(String(email).toLowerCase());
};

/**
 * Check if string is possibly zipcode
 * @param zipcode
 * @returns {boolean}
 */
export const isAZipCode = (zipcode: string) => {
    const regex = /^(?:0[1-9]|[1-8]\d|9[0-8])\d{3}$/;
    return regex.test(String(zipcode).toLowerCase());
};

/**
 * Add debounce timeout
 * @param fn - The function to be debounced
 * @param ms - The amount of time in milliseconds to delay
 * @returns A debounced version of the given function
 */

// Define a generic type for the debounced function
export const debounce = <T extends (...args: unknown[]) => unknown>(
    fn: T,
    ms: number,
): ((...args: Parameters<T>) => void) => {
    let timer: NodeJS.Timeout | null = null;

    return (...args: Parameters<T>) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            timer = null;
            fn.apply(this, args);
        }, ms);
    };
};

/**
 * Check if location is a sublocation of precedent location
 */
export const isNewRootLocation = (nextPath: string, prevPath: string) => {
    const splittedPrevMatchPath = prevPath ? prevPath.split('/')[1] : '';
    const splittedPath = nextPath ? nextPath.split('/')[1] : '';

    if (splittedPrevMatchPath === '' || splittedPath === '') {
        return false;
    }

    return splittedPath !== splittedPrevMatchPath;
};

/** *
 * Parse JWT token
 *
 * @param token
 * @returns {any}
 */
export const parseJWT = (token: string) => {
    if (!token) return {};

    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
        atob(base64)
            .split('')
            .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
            .join(''),
    );

    return JSON.parse(jsonPayload);
};

/**
 * Check if string is possibly SIREN
 * @param siren
 * @param isBelgianSiren - Optional flag to use Belgian SIREN format (10 digits instead of 9)
 * @returns {boolean}
 */
export const isASiren = (siren: string, isBelgianSiren?: boolean) => {
    const maxDigits = isBelgianSiren ? 10 : 9;
    const regex = new RegExp(`^[0-9]{${maxDigits}}$`, 'g');
    return regex.test(String(siren).toLowerCase());
};

/**
 * Check if string is possibly SIRET
 * @param siret - The string or number to check
 * @returns {boolean} - Whether the input is a valid SIRET number
 */
export const isASiret = (siret: string | number): boolean => {
    const regex = /^[0-9]{14}$/g;
    return regex.test(String(siret).toLowerCase());
};

/**
 * Format a string as a French SIRET number (groups of 3-3-3-5 digits).
 * Non-digits are removed, max 14 digits.
 * @param input - String to format
 * @returns Formatted SIRET number
 */
export const formatSiretNumber = (input: string): string => {
    if (!input) return;

    const digits = input.replace(/\D+/g, '').slice(0, 14);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
};

/**
 * Creates a promise that resolves after a specified number of milliseconds.
 * @param ms - The number of milliseconds to wait before resolving the promise
 * @returns {Promise<void>} - A promise that resolves after the specified time
 */
export const sleep = (ms: number): Promise<void> =>
    new Promise((r) => {
        setTimeout(r, ms);
    });

/**
 * Check if string is possibly a TVA number
 * (2-letter country code + 2-char control key that may be alphanumeric + 9-digit SIREN)
 * @param tva
 * @returns {boolean}
 */
export const isVATNumber = (tva: string): boolean => {
    const regex = /^[A-Z]{2}[0-9A-Z]{2}\d{9}$/i;
    return regex.test(tva);
};

/**
 * Formats a VAT number by removing spaces, converting to uppercase,
 * and inserting a space after the country code if present.
 *
 * @param input - The VAT number to format.
 * @returns The formatted VAT number.
 */
export const formatVATNumber = (input: string): string => {
    const cleaned = input.replace(/\s+/g, '').toUpperCase();
    return cleaned.length <= 2 ? cleaned : `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
};
